'use client'

import HelmetIcon from '@/components/icons/helmet-icon'
import RouteIcon from '@/components/icons/route-icon'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getChampionshipEventDriverStats } from '@/helpers/championship'
import { cn } from '@/lib/utils'
import { LayoutGrid } from 'lucide-react'
import { useMemo } from 'react'

export default function EventCardHydration({
  event,
  extraEventEventInfo,
  extraEventInfoLoading,
  isAfterFinished,
  mainSession,
}) {
  return (
    <>
      {extraEventInfoLoading ? (
        <>
          <div className="flex w-full items-center align-middle">
            <Skeleton className="h-4 w-full" />
          </div>
          <div className="divide mb-3 mt-3 bg-neutral-blue-700 desktop-xs:mb-4 desktop-xs:mt-4" />
          <div className="flex w-full items-center align-middle">
            <Skeleton className="h-4 w-full" />
          </div>
          <div className="divide mb-3 mt-3 bg-neutral-blue-700 desktop-xs:mb-4 desktop-xs:mt-4" />
          <div className="flex w-full items-center align-middle">
            <Skeleton className="h-4 w-full" />
          </div>
        </>
      ) : (
        <>
          <EventSessions extraEventEventInfo={extraEventEventInfo} />

          <div className="divide mb-3 mt-3 bg-neutral-blue-700 desktop-xs:mb-4 desktop-xs:mt-4" />

          <EventTrack extraEventEventInfo={extraEventEventInfo} />

          <div className="divide mb-3 mt-3 bg-neutral-blue-700 desktop-xs:mb-4 desktop-xs:mt-4" />

          <EventRegisteredDrivers
            {...{
              event,
              extraEventEventInfo,
              extraEventInfoLoading,
              mainSession,
              isAfterFinished,
            }}
          />
        </>
      )}
    </>
  )
}

function EventSessions({ extraEventEventInfo }) {
  if (!extraEventEventInfo || !(extraEventEventInfo?.sessions?.length > 0)) {
    return (
      <div className="flex w-full items-center align-middle">
        <Skeleton className="h-4 w-full" />
      </div>
    )
  }

  const sessionListInString = extraEventEventInfo.sessions?.length
    ? extraEventEventInfo.sessions.map((s) => s.type[0]).join(' • ')
    : ''

  return (
    <div className="flex w-full items-center gap-2 align-middle">
      <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
        <LayoutGrid className="mx-auto size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
      </div>
      <div className="flex w-full items-center justify-between gap-2 align-middle">
        <p className="text-p-1 font-semibold text-shades-0 desktop-xs:text-p-2">
          Sessions
        </p>

        <div className="truncate-1 flex text-p-1 font-normal capitalize desktop-xs:text-p-2">
          {sessionListInString}
        </div>
      </div>
    </div>
  )
}

function EventTrack({ extraEventEventInfo }) {
  if (!extraEventEventInfo) {
    return (
      <div className="flex w-full items-center align-middle">
        <Skeleton className="h-4 w-full" />
      </div>
    )
  }

  return (
    <div className="flex w-full items-center gap-2 align-middle">
      <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
        <RouteIcon className="mx-auto size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
      </div>
      <div className="flex w-full items-center justify-between gap-4 align-middle">
        <p className="text-p-1 font-semibold text-shades-0 desktop-xs:text-p-2">
          Track
        </p>

        {extraEventEventInfo?.track?.name ||
        extraEventEventInfo?.trackMeta?.name ? (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <p className="truncate-1 break-all text-p-1 desktop-xs:text-p-2">
                  {extraEventEventInfo?.track?.name ||
                    extraEventEventInfo.trackMeta.name}
                </p>
              </TooltipTrigger>
              <TooltipContent align="end">
                <p className="text-p-2">
                  {extraEventEventInfo?.track?.name ||
                    extraEventEventInfo.trackMeta.name}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <p className="truncate-1 break-all text-p-1 desktop-xs:text-p-2">
            No track set
          </p>
        )}
      </div>
    </div>
  )
}

function EventRegisteredDrivers({
  event,
  extraEventEventInfo,
  extraEventInfoLoading,
  mainSession,
  isAfterFinished,
}) {
  const { attendees, gridPositions, gridPositionsLabel, isGridUnlimited } =
    useMemo(() => {
      const eventInfo = { ...event, ...extraEventEventInfo }
      return getChampionshipEventDriverStats(
        extraEventInfoLoading ? { attendees: [] } : eventInfo,
        eventInfo.championship,
      )
    }, [event, extraEventEventInfo, extraEventInfoLoading])

  return (
    <div className="flex w-full items-center gap-2 align-middle">
      <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
        <HelmetIcon className="mx-auto size-[14px] stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
      </div>
      <div className="flex w-full items-center justify-between gap-2 align-middle">
        <p className="text-p-1 font-semibold text-shades-0 desktop-xs:text-p-2">
          Drivers
        </p>

        {isAfterFinished ? (
          <span className="truncate-1 text-p-1 text-error-500 desktop-xs:text-p-2">
            • Closed
          </span>
        ) : (
          <span
            className={cn(
              `truncate-1 text-p-1 text-success-500 desktop-xs:text-p-2`,
              {
                'text-warning-500!':
                  !isGridUnlimited &&
                  attendees.length > gridPositions / 2 &&
                  mainSession?.type !== 'hotlap',
              },
            )}
          >
            • {attendees.length}{' '}
            {mainSession?.type !== 'hotlap' && `/ ${gridPositionsLabel}`}
          </span>
        )}
      </div>
    </div>
  )
}
